<script setup lang="ts">
/**
 * The shadow panel every element shares: the type thumbnails, the offset/angle/blur sliders and the
 *  color options, wired to the shadow API of whatever element the parent points at.
 *
 * Giving a shadow to a new element is a fabric class implementing ShadowElement plus a thin wrapper
 *  that renders this component, not another copy of the panel.
 */
import { computed } from 'vue';
import CSlider from '@/components/shared/CSlider.vue';
import ElementLibrarySelector from '@/components/DesignStudio/ElementLibrarySelector.vue';
import ColorOptionsClipboardButtons from '@/components/DesignStudio/ColorOptionsClipboardButtons.vue';
import { LibraryTypeEnum } from '@/utils/enums/libraryType.enum';
import { ShadowTypeEnum } from '@/utils/enums/shadowType.enum';
import type { ShadowSettingsApi } from '@/composables/shadowSettings';
import { useColorOptionsClipboard } from '@/composables/useColorOptionsClipboard';
import { useToaster } from '@/composables/toaster';
import { DatadogEvent, fireDatadogLog } from '@/utils/datadogUtils';
import { recordFeatureUsage, TrackedFeature } from '@/utils/featureUsage';

const COLOR_TYPE = LibraryTypeEnum.Color;
const BLURRED_SHADOW = ShadowTypeEnum.Blurred;

type ShadowVariant = 'text' | 'image';

// The thumbnails preview the shadow on the element it belongs to, so each variant gets its own art
const shadowThumbnails: Record<ShadowVariant, Record<ShadowTypeEnum, string>> = {
    text: {
        [ShadowTypeEnum.Drop]: require('@/assets/images/drop-shadow.svg'),
        [ShadowTypeEnum.Blurred]: require('@/assets/images/blurred-drop-shadow.svg')
    },
    image: {
        [ShadowTypeEnum.Drop]: require('@/assets/images/drop-shadow-image.svg'),
        [ShadowTypeEnum.Blurred]: require('@/assets/images/blur-shadow-image.svg')
    }
};

const shadowTypes = [
    { type: ShadowTypeEnum.Drop, label: 'Drop shadow' },
    { type: ShadowTypeEnum.Blurred, label: 'Blurred drop shadow' }
];

const props = withDefaults(
    defineProps<{
        /** What useShadowSettings() returned for the selected element. */
        shadow: ShadowSettingsApi;
        cmykMode?: boolean;
        /** In the step 2 preview only the color options can be changed, so everything else is hidden */
        isPreview?: boolean;
        /** Which set of thumbnails to show for the shadow types. */
        variant?: ShadowVariant;
    }>(),
    {
        isPreview: false,
        variant: 'text'
    }
);

const shadowOptions = computed(() =>
    shadowTypes.map((option) => ({ ...option, img: shadowThumbnails[props.variant][option.type] }))
);

const model = computed(() => props.shadow.model);
const colors = computed(() => props.shadow.colors);
const disabled = computed(() => props.shadow.disabled.value);

const selectType = (type: ShadowTypeEnum) => {
    if (disabled.value) return;
    props.shadow.changeShadowType(type);
};

/* Copy/paste color options, shared with Fill/Stroke (text) and Vector single-color */
const clipboard = useColorOptionsClipboard();
const toast = useToaster();

// Copy/paste only makes sense once a shadow exists - the whole section is hidden until then
const canPasteColors = computed(() => {
    const clip = clipboard.clip();
    return !disabled.value && !!clip && !clip.hasNonSolidColor;
});

const onCopyColors = () => {
    clipboard.copy({
        colors: new Map(model.value.shadowColorsMap),
        libraryId: model.value.shadowColorLibraryId
    });
    fireDatadogLog(DatadogEvent.CopyDsColorOptions);
    toast.show('Shadow colors copied');
};

const onPasteColors = () => {
    const clip = clipboard.clip();
    if (!canPasteColors.value || !clip) return;
    colors.value.pasteColors(clip.colors, clip.libraryId);
    fireDatadogLog(DatadogEvent.PasteDsColorOptions);
    recordFeatureUsage(TrackedFeature.ColorsPasteShadow);
    toast.show('Shadow colors pasted');
};
</script>

<template>
    <div class="shadow-settings-wrapper">
        <section v-if="!props.isPreview" class="shadow-section">
            <div class="shadow-grid-wrapper" role="group" aria-label="Shadow type">
                <div
                    v-for="option in shadowOptions"
                    :key="option.type"
                    role="button"
                    :tabindex="disabled ? -1 : 0"
                    :aria-pressed="model.shadowType === option.type"
                    :aria-disabled="disabled"
                    :aria-label="option.label"
                    class="shadow-thumb"
                    :class="{
                        selected: model.shadowType === option.type,
                        disabled: disabled
                    }"
                    @click="selectType(option.type)"
                    @keydown.enter.prevent="selectType(option.type)"
                    @keydown.space.prevent="selectType(option.type)"
                >
                    <img :src="option.img" alt="" class="shadow-image" />
                </div>
            </div>
        </section>

        <section v-if="model.shadowType != null" class="shadow-section">
            <color-options-clipboard-buttons
                title="Color"
                :can-copy="!disabled"
                :can-paste="canPasteColors"
                @copy="onCopyColors"
                @paste="onPasteColors"
            />
            <div class="border-bottom" />
            <element-library-selector
                :disabled="disabled"
                :element-list="model.shadowColorsMap"
                :library-id="model.shadowColorLibraryId"
                :library-type="COLOR_TYPE"
                :selected-position="model.shadowColorOption"
                :cmyk="props.cmykMode"
                @pick-library="colors.pickColorLibrary()"
                @remove-library="colors.removeColorLibrary()"
                @option-selected="colors.onColorSelected"
                @remove-map-option="colors.removeColorOption($event)"
                @load-option="(option, value) => colors.loadColorIntoElement(option, value)"
                @options-reordered="colors.onColorOptionsReordered($event)"
                @preview-selection="colors.previewColorOption($event)"
            />
        </section>

        <section v-if="model.shadowType != null && !props.isPreview" class="shadow-section">
            <h6>Settings</h6>
            <div class="border-bottom" />
            <div class="row align-items-center">
                <div class="col-6">
                    <label>Offset: {{ model.shadowOffset }}%</label>
                    <c-slider
                        :value="model.shadowOffset"
                        formatter="{value}%"
                        :max="100"
                        :disabled="disabled"
                        @input="shadow.changeShadowOffset"
                    />
                </div>
                <div class="col-6">
                    <label>Angle: {{ model.shadowAngle }}°</label>
                    <c-slider
                        :value="model.shadowAngle"
                        formatter="{value}°"
                        :max="365"
                        :disabled="disabled"
                        @input="shadow.changeShadowAngle"
                    />
                </div>
            </div>
            <div class="row align-items-center">
                <div v-if="model.shadowType === BLURRED_SHADOW" class="col-6">
                    <label>Blur: {{ model.shadowBlur }}%</label>
                    <c-slider
                        :value="model.shadowBlur"
                        formatter="{value}%"
                        :max="100"
                        :disabled="disabled"
                        @input="shadow.changeShadowBlur"
                    />
                </div>
            </div>
        </section>
    </div>
</template>

<style lang="scss" scoped>
@import 'styles/utilities/functions';

.shadow-settings-wrapper {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    .shadow-section {
        display: flex;
        flex-direction: column;
        gap: 0.625rem;
        h6 {
            font-size: 0.875rem;
            margin-bottom: 0;
        }
    }
}

.shadow-grid-wrapper {
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
    align-items: center;
}

.shadow-thumb {
    cursor: pointer;
    border: 1px solid get-color('gray-light');
    border-radius: 6px;
    padding: 6px;
    text-align: center;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 80px;
    height: 80px;
    transition: box-shadow 0.12s ease, border-color 0.12s ease, transform 0.06s ease;
    user-select: none;
    outline: none;
    opacity: 1;

    &.selected {
        border: 2px solid get-color('purple');
        box-shadow: 0 2px 6px rgba(get-color('purple'), 0.08);
    }

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 0 0 1px get-color('purple');
    }

    &:focus-visible {
        box-shadow: 0 0 0 2px get-color('purple');
    }

    &.disabled {
        pointer-events: none;
        opacity: 0.4;
        cursor: not-allowed;
    }
}

.shadow-image {
    background-color: get-color('white');
    max-width: 100%;
    object-fit: cover;
    padding: 0;
    display: block;
}
</style>
